import { Button, Text, useToast } from '@razorpay/blade/components';
import {
  FormWrapper,
  StepActionsWrapper,
  StepContentWrapper,
  StepHeader,
} from 'components/WizardViews/components';
import {
  ReviewSection,
  ReviewSectionContainer,
  ReviewSectionHeader,
  ReviewSectionItem,
  ReviewSectionItemContainer,
} from 'components/WizardViews/components/review';
import { useWizardInfo } from 'components/WizardViews/providers/WizardInfoProvider';
import { useFormWithProvider } from 'forms';
import React, { FormEvent } from 'react';
import { useMutation } from 'react-query';
import api from 'api/index';
import { parseGraphqlErrorFromExtensions } from 'utils/Api';
import { AppError } from 'utils/AppError';
import { PAYMENT_STATUS_TO_DISBURSEMENT_STATUS, bonusSteps, getTdsSettingLabel } from './constants';
import { BonusFormData } from './types';
import {
  buildCreateBonusAssignmentInput,
  findBonusType,
  formatBonusAmountForDisplay,
  formatPayrollMonth,
  getSelectedPaymentStatus,
  resolveClawbackPeriod,
  rupeesToPaise,
} from './utils';
import { useBonusTypes } from './hooks/useBonusTypes';

const ReviewStep: React.FC = () => {
  const { formData } = useFormWithProvider<BonusFormData>();
  const { setCurrentStep, closeWizard, onSuccess } = useWizardInfo();
  const { bonusTypes } = useBonusTypes();
  const toast = useToast();

  const selectedBonus = findBonusType(bonusTypes, formData.bonusType);
  const selectedPaymentStatus = getSelectedPaymentStatus(formData.paymentStatus);
  const hasClawback = formData.hasClawback === 'true';
  const isEdit = Boolean(formData.assignmentId);
  const initialPaymentStatus = formData.initialPaymentStatus
    ? PAYMENT_STATUS_TO_DISBURSEMENT_STATUS[formData.initialPaymentStatus]
    : undefined;

  const handleSuccess = (content: string) => () => {
    toast.show({ content, color: 'positive' });
    onSuccess?.();
    closeWizard();
  };

  const onError = (error: typeof AppError) => {
    toast.show({ content: parseGraphqlErrorFromExtensions(error), color: 'negative' });
  };

  const createBonusAssignmentMutation = useMutation(
    api.bonusAssignment.mutations.createBonusAssignment,
    { onSuccess: handleSuccess('Bonus allocated successfully'), onError },
  );

  const updateBonusAssignmentMutation = useMutation(
    api.bonusAssignment.mutations.updateBonusAssignment,
    { onSuccess: handleSuccess('Bonus updated successfully'), onError },
  );

  const isSubmitting =
    createBonusAssignmentMutation.isLoading || updateBonusAssignmentMutation.isLoading;

  const renderValue = (value: string) => (
    <Text color="surface.text.gray.subtle" textAlign="right">
      {value}
    </Text>
  );

  const bailToFirstStep = () => {
    toast.show({
      content: 'Some bonus details are missing. Please review the earlier steps.',
      color: 'negative',
    });
    setCurrentStep(bonusSteps.step1);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    if (isEdit) {
      const assignmentId = formData.assignmentId;
      if (!assignmentId || !formData.bonusAmount) {
        bailToFirstStep();
        return;
      }
      const amount = Number(formData.bonusAmount);
      if (!Number.isFinite(amount) || amount <= 0) {
        bailToFirstStep();
        return;
      }

      const clawbackPeriod = resolveClawbackPeriod(hasClawback, formData.clawbackMonths);
      if (clawbackPeriod === null) {
        bailToFirstStep();
        return;
      }

      const nextPaymentStatus = formData.paymentStatus
        ? PAYMENT_STATUS_TO_DISBURSEMENT_STATUS[formData.paymentStatus]
        : undefined;

      const amountChanged = formData.bonusAmount !== formData.initialBonusAmount;
      const payoutMonthChanged = formData.payoutMonth !== formData.initialPayoutMonth;

      updateBonusAssignmentMutation.mutate({
        input: {
          id: assignmentId,
          ...(amountChanged ? { amount: rupeesToPaise(amount).toString() } : {}),
          remarks: formData.remark || undefined,
          clawback_applicable: hasClawback,
          clawback_period: clawbackPeriod,
          ...(payoutMonthChanged && formData.payoutMonth
            ? { payroll_month: formData.payoutMonth }
            : {}),
          ...(nextPaymentStatus && nextPaymentStatus !== initialPaymentStatus
            ? { payment_status: nextPaymentStatus }
            : {}),
        },
      });
      return;
    }

    const input = buildCreateBonusAssignmentInput(formData);
    if (!input) {
      bailToFirstStep();
      return;
    }
    createBonusAssignmentMutation.mutate({ input });
  };

  return (
    <FormWrapper onSubmit={handleSubmit}>
      <StepContentWrapper noBgIllustration width="594px" gap="spacing.7">
        <StepHeader title="Review Details" subtitle="" marginBottom="spacing.7" />

        <ReviewSectionContainer>
          <ReviewSection>
            <ReviewSectionHeader
              title="Bonus Details"
              subtitle=""
              onEditClick={() => setCurrentStep(bonusSteps.step1)}
            />
            <ReviewSectionItemContainer>
              <ReviewSectionItem label="Bonus" value={renderValue(selectedBonus?.title ?? '-')} />
              <ReviewSectionItem
                label="Tax Deduction"
                value={renderValue(getTdsSettingLabel(selectedBonus?.taxDeductionBehaviour) ?? '-')}
              />
              <ReviewSectionItem
                label="Bonus Amount"
                value={renderValue(
                  formData.bonusAmount ? `₹${formatBonusAmountForDisplay(formData.bonusAmount)}` : '-',
                )}
              />
              <ReviewSectionItem
                label="Payment Status"
                value={renderValue(selectedPaymentStatus?.title ?? '-')}
              />
              <ReviewSectionItem
                label="Payout Month"
                value={renderValue(formatPayrollMonth(formData.payoutMonth) || '-')}
              />
              <ReviewSectionItem label="Remarks" value={renderValue(formData.remark || '-')} />
            </ReviewSectionItemContainer>
          </ReviewSection>

          <ReviewSection>
            <ReviewSectionHeader
              title="Clawback Details"
              subtitle=""
              onEditClick={() => setCurrentStep(bonusSteps.step2)}
            />
            <ReviewSectionItemContainer>
              <ReviewSectionItem
                label="Clawback Rule"
                value={renderValue(hasClawback ? 'Yes' : 'No')}
              />
              {hasClawback && (
                <ReviewSectionItem
                  label="Number of Months"
                  value={renderValue(formData.clawbackMonths || '-')}
                />
              )}
            </ReviewSectionItemContainer>
          </ReviewSection>
        </ReviewSectionContainer>
      </StepContentWrapper>
      <StepActionsWrapper width="594px">
        <Button
          variant="secondary"
          onClick={() => setCurrentStep(bonusSteps.step2)}
          isDisabled={isSubmitting}>
          Back
        </Button>
        <Button type="submit" isLoading={isSubmitting}>
          {isEdit ? 'Save Changes' : 'Assign Bonus'}
        </Button>
      </StepActionsWrapper>
    </FormWrapper>
  );
};

export default ReviewStep;
